import React from 'react';
import { Button } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import { extractApiErrorMessage } from '../../utils';
import { LifecycleTimeline } from './LifecycleTimeline';
import { ReadinessSummary } from './ReadinessSummary';
import { AntibioticImpactBanner } from './AntibioticImpactBanner';
import { DigitalTwinPredictionContext } from './DigitalTwinPredictionContext';
import { DigitalTwinForecastChart } from './DigitalTwinForecastChart';
import { DigitalTwinHeader } from './DigitalTwinHeader';
import { DigitalTwinSkeleton } from './DigitalTwinSkeleton';
import { EconomicForecastPanel } from './EconomicForecastPanel';
import type { LifecyclePrediction } from './types';
import css from './DigitalTwinView.module.css';

interface DigitalTwinModuleProps {
  animalId: string;
}

export const DigitalTwinModule: React.FC<DigitalTwinModuleProps> = ({ animalId }) => {
  const { t } = useTranslation();
  const apiService = React.useMemo(() => new ApiService(), []);

  const [prediction, setPrediction] = React.useState<LifecyclePrediction | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const mountedRef = React.useRef(true);

  const loadPrediction = React.useCallback(async () => {
    if (!animalId) return;
    setIsBusy(true);
    setLoaded(false);
    setError(null);
    try {
      const data = await apiService.getLifecyclePrediction(animalId);
      if (mountedRef.current) {
        setPrediction(data);
        setLoaded(true);
      }
    } catch (e: unknown) {
      if (mountedRef.current) {
        setError(extractApiErrorMessage(e as Error, { fallback: t('f-com-api-error') }));
      }
    } finally {
      if (mountedRef.current) setIsBusy(false);
    }
  }, [animalId, apiService, t]);

  React.useEffect(() => {
    mountedRef.current = true;
    loadPrediction();
    return () => {
      mountedRef.current = false;
    };
  }, [loadPrediction]);

  const milestones = React.useMemo(
    () =>
      prediction
        ? {
            birth: prediction.birth,
            weaned: prediction.weaned,
            tenWeeks: prediction.tenWeeks,
            slaughterReady: prediction.slaughterReady
          }
        : null,
    [prediction?.birth.date, prediction?.weaned.date, prediction?.tenWeeks.date, prediction?.slaughterReady.date]
  );

  if (isBusy && !loaded && !error) {
    return (
      <div className={css.dashboard}>
        <DigitalTwinSkeleton />
      </div>
    );
  }

  if (error && !prediction) {
    return (
      <div className={css.dashboard}>
        <div className={css.skeletonRoot}>
          <div className={css.errorPanel}>
            <p className={css.errorText}>{t('digitalTwin.loadError')}</p>
            <Button size="small" variant="outlined" color="primary" onClick={loadPrediction}>
              {t('digitalTwin.retry')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!prediction || !milestones) return null;

  return (
    <div className={css.dashboard}>
      <div className={css.dashboardInner}>
        <DigitalTwinHeader prediction={prediction} />

        <section className={css.section} aria-labelledby="dt-section-lifecycle">
          <div className={css.sectionHeader}>
            <h3 id="dt-section-lifecycle" className={css.sectionTitle}>
              {t('digitalTwin.section.lifecycle')}
            </h3>
          </div>
          <LifecycleTimeline milestones={milestones} />
        </section>

        <section className={css.section} aria-labelledby="dt-section-metrics">
          <div className={css.sectionHeader}>
            <h3 id="dt-section-metrics" className={css.sectionTitle}>
              {t('digitalTwin.section.metrics')}
            </h3>
            <p className={css.sectionHint}>{t('digitalTwin.section.metricsHint')}</p>
          </div>
          <ReadinessSummary prediction={prediction} />
        </section>

        <AntibioticImpactBanner antibioticImpact={prediction.antibioticImpact} />

        <EconomicForecastPanel prediction={prediction} />

        <section className={css.section} aria-labelledby="dt-section-context">
          <div className={css.sectionHeader}>
            <h3 id="dt-section-context" className={css.sectionTitle}>
              {t('digitalTwin.section.modelContext')}
            </h3>
          </div>
          <DigitalTwinPredictionContext prediction={prediction} />
        </section>

        <DigitalTwinForecastChart prediction={prediction} />
      </div>
    </div>
  );
};
